import React, { useCallback, useEffect, useMemo, useState } from "react"

import { format } from "date-fns"
import { ArrowUpDown, Ban, CornerDownRight, Search, X } from "lucide-react"
import { toast } from "sonner"

import { EmptyState } from "@/components/shared/EmptyState"
import { LoadingState } from "@/components/shared/LoadingState"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { StatusPill } from "@/components/ui/status-pill"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  useDrbRowAssignments,
  useRouteInstancesForDate,
  useShiftExceptions,
  useUpdateDrbAssignment,
  type DrbOption,
  type DrbRowAssignment,
} from "@/hooks/useCollections"
import { formatDayShortYear } from "@/lib/dateFormats"
import { buildCoverIndex, buildCoveredIndex } from "@/lib/shiftCover"
import {
  assignmentState,
  assignmentUrgency,
  coverNotCheckedReason,
  indexDriversByPersonId,
  matchPlannedDriverOption,
  plannedDriverSeat,
  plannedDriverClipboardValue,
  type AssignmentUrgency,
  type PlannedDriver,
} from "@/lib/routeAssignmentRows"
import { cn } from "@/lib/utils"
import { Route, RouteInstance, toRouteLegacy } from "@/types/collections"

import { AssignmentCombobox } from "./AssignmentCombobox"

type AssignmentField = "driver" | "vehicle" | "trailer" | "second_driver"

type ColumnKey = "code" | "start" | "planned" | "driver" | "vehicle" | "trailer"

type SortKey = ColumnKey | "assignment"

/** A pick Airtable has not confirmed yet; only the changed fields are present. */
type RowOverride = Partial<Record<AssignmentField, { id: string | null; name: string | null }>>

/**
 * A grid, not a <table>: combobox popovers must escape rows under a sticky header and horizontal
 * scroll.
 */
const GRID_TEMPLATE =
  "minmax(14rem,1.3fr) 5rem minmax(11rem,1fr) minmax(12rem,1.2fr) minmax(11rem,1.1fr) minmax(8rem,0.8fr) 8.5rem"

const CELL = "flex items-center min-w-0 border-r border-palette-jungleGreen100 px-2.5 py-2"

const KEY_CELL = "bg-palette-jungleGreen000/70"

const COLUMNS: { key: ColumnKey; label: string }[] = [
  { key: "code", label: "Route code" },
  { key: "start", label: "Start" },
  { key: "planned", label: "Planned shift driver" },
  { key: "driver", label: "Driver" },
  { key: "vehicle", label: "Vehicle" },
  { key: "trailer", label: "Trailer" },
]

const URGENCY_ROW: Record<Exclude<AssignmentUrgency, "none">, string> = {
  overdue: "bg-variance-late-tint hover:bg-variance-late-tint",
  soon: "bg-variance-at-risk-tint hover:bg-variance-at-risk-tint",
}

const URGENCY_BAR: Record<Exclude<AssignmentUrgency, "none">, string> = {
  overdue: "bg-variance-late",
  soon: "bg-variance-at-risk",
}

const URGENCY_NOTE: Record<Exclude<AssignmentUrgency, "none">, string> = {
  overdue: "Start time passed, still not assigned",
  soon: "Starts soon, still not assigned",
}

const SortHeader: React.FC<{
  label: string
  sortKey: SortKey
  activeSort: SortKey
  sortDesc: boolean
  onSort: (key: SortKey) => void
}> = ({ label, sortKey, activeSort, sortDesc, onSort }) => {
  const active = activeSort === sortKey
  return (
    <button
      type="button"
      onClick={() => onSort(sortKey)}
      className="flex min-w-0 flex-1 items-center gap-1 text-left hover:text-palette-jungleGreen900"
      aria-label={`Sort by ${label}`}
      aria-sort={active ? (sortDesc ? "descending" : "ascending") : "none"}
    >
      <span className="truncate">{label}</span>
      <ArrowUpDown
        size={11}
        className={cn("flex-none", active ? "text-palette-jungleGreen900" : "opacity-30")}
      />
      {active && <span className="sr-only">{sortDesc ? "descending" : "ascending"}</span>}
    </button>
  )
}

const Chip: React.FC<{ children: React.ReactNode; title?: string; className?: string }> = ({
  children,
  title,
  className,
}) => (
  <span
    title={title}
    className={cn(
      "truncate rounded bg-palette-jungleGreen100 px-2 py-0.5 text-sm text-palette-jungleGreen900",
      className
    )}
  >
    {children}
  </span>
)

/**
 * Saves go to the Airtable DRB record and collections_route_instances, not Samsara (its sync can't
 * take driver/vehicle yet).
 */
const RouteAssignmentListView: React.FC = () => {
  const today = useMemo(() => new Date(), [])
  const dateStr = format(today, "yyyy-MM-dd")

  const { data: routeInstancesData, isLoading: isRoutesLoading } = useRouteInstancesForDate(dateStr)
  const updateAssignment = useUpdateDrbAssignment()

  const [search, setSearch] = useState("")
  const [showCancelled, setShowCancelled] = useState(false)
  const [sortKey, setSortKey] = useState<SortKey>("start")
  const [sortDesc, setSortDesc] = useState(false)
  const [openPicker, setOpenPicker] = useState<string | null>(null)
  const [overrides, setOverrides] = useState<Record<string, RowOverride>>({})
  const [saving, setSaving] = useState<Record<string, boolean>>({})
  /** `${routeId}:${personId|name}`: a double-manned row has two chips. */
  const [copiedKey, setCopiedKey] = useState<string | null>(null)

  /** Minute tick, matching the timeline; the highlight depends on time of day. */
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(interval)
  }, [])

  /** Cancelled routes are hidden by default and read-only when shown. */
  const routes: Route[] = useMemo(() => {
    if (!routeInstancesData) return []
    const all = routeInstancesData.map((ri: RouteInstance) => toRouteLegacy(ri))
    return showCancelled ? all : all.filter((r) => r.status !== "cancelled")
  }, [routeInstancesData, showCancelled])

  const routeCodes = useMemo(() => routes.map((r) => r.code), [routes])
  const {
    drivers,
    vehicles,
    trailers,
    byRouteCode,
    isLoading: isDrbLoading,
  } = useDrbRowAssignments(routeCodes)

  /** Only unstarted routes appear: the cover check stops reporting once a route rolls. */
  const { data: shiftData, error: shiftError } = useShiftExceptions({
    from: dateStr,
    to: dateStr,
  })

  /** Keyed by instance id: two instances of a route share a code but not a booking. */
  const coveredByInstance = useMemo(
    () => buildCoveredIndex(shiftData?.covered),
    [shiftData?.covered]
  )
  const coverByInstance = useMemo(
    () => buildCoverIndex(shiftData?.exceptions),
    [shiftData?.exceptions]
  )

  /** A list: a double-manned route reports one covered row per seat. Deduped by shift. */
  const plannedFor = useCallback(
    (routeId: string): PlannedDriver[] => {
      const covered = coveredByInstance.get(routeId) ?? []
      if (covered.length > 0) {
        return covered
          .filter((c) => c.shiftPersonName)
          .map((c) => ({ name: c.shiftPersonName as string, personId: c.shiftPersonId ?? null }))
      }
      const seen = new Set<string>()
      const people: PlannedDriver[] = []
      for (const exception of coverByInstance.get(routeId) ?? []) {
        if (!exception.shiftPersonName) continue
        const key = exception.shiftId ?? exception.shiftPersonName
        if (seen.has(key)) continue
        seen.add(key)
        people.push({ name: exception.shiftPersonName, personId: exception.shiftPersonId ?? null })
      }
      return people
    },
    [coveredByInstance, coverByInstance]
  )

  /** See matchPlannedDriverOption: the copy across resolves by identifier, never by name. */
  const driverByPersonId = useMemo(() => indexDriversByPersonId(drivers), [drivers])

  const secondDriverOptionIdByCode = useMemo(() => {
    const map = new Map<string, string | null>()
    for (const route of routes) {
      const personId = route.secondDriverId?.toString().trim()
      map.set(route.code, (personId && driverByPersonId.get(personId)?.id) || null)
    }
    return map
  }, [routes, driverByPersonId])

  /**
   * Drop overrides once the server agrees, or they would mask later reassignments from the refetch.
   */
  useEffect(() => {
    setOverrides((current) => {
      let changed = false
      const next: Record<string, RowOverride> = {}
      for (const [routeCode, fields] of Object.entries(current)) {
        const assignment = byRouteCode.get(routeCode)
        const kept: RowOverride = {}
        for (const [field, pick] of Object.entries(fields) as [
          AssignmentField,
          { id: string | null; name: string | null },
        ][]) {
          // The second driver has no DRB field, so it reconciles against the route instance.
          const serverId =
            field === "second_driver"
              ? (secondDriverOptionIdByCode.get(routeCode) ?? null)
              : field === "driver"
                ? (assignment?.driverId ?? null)
                : field === "vehicle"
                  ? (assignment?.vehicleId ?? null)
                  : (assignment?.trailerId ?? null)
          if (serverId === pick.id) changed = true
          else kept[field] = pick
        }
        if (Object.keys(kept).length > 0) next[routeCode] = kept
      }
      return changed ? next : current
    })
  }, [byRouteCode, secondDriverOptionIdByCode])

  /** The second driver reads from the route instance by person_id; Airtable has no field for it. */
  const cellValue = useCallback(
    (route: Route, field: AssignmentField, assignment: DrbRowAssignment | undefined) => {
      const override = overrides[route.code]?.[field]
      if (override) return override
      if (field === "driver")
        return { id: assignment?.driverId ?? null, name: assignment?.driverName ?? null }
      if (field === "vehicle")
        return { id: assignment?.vehicleId ?? null, name: assignment?.vehicleName ?? null }
      if (field === "second_driver") {
        const personId = route.secondDriverId?.toString().trim()
        const option = personId ? driverByPersonId.get(personId) : undefined
        // The stored label covers people no longer in the option list.
        return { id: option?.id ?? null, name: option?.name ?? route.secondDriverName ?? null }
      }
      return { id: assignment?.trailerId ?? null, name: assignment?.trailerName ?? null }
    },
    [overrides, driverByPersonId]
  )

  /** The override goes in before the request and is removed only on failure. */
  const handleAssign = useCallback(
    async (route: Route, field: AssignmentField, id: string | null, name: string | null) => {
      const assignment = byRouteCode.get(route.code)
      const cellKey = `${route.id}:${field}`

      setOverrides((current) => ({
        ...current,
        [route.code]: { ...current[route.code], [field]: { id, name } },
      }))
      setSaving((current) => ({ ...current, [cellKey]: true }))

      try {
        await updateAssignment.mutateAsync({
          routeCode: route.code,
          airtableRecordId: assignment?.airtableRecordId,
          routeInstanceId: route.id,
          ...(field === "driver" ? { driverId: id } : {}),
          ...(field === "vehicle" ? { vehicleId: id } : {}),
          ...(field === "trailer" ? { trailerId: id } : {}),
          ...(field === "second_driver" ? { secondDriverId: id } : {}),
        })
      } catch (error) {
        console.error(`Error assigning ${field} on ${route.code}:`, error)
        setOverrides((current) => {
          const forRoute = { ...current[route.code] }
          delete forRoute[field]
          return { ...current, [route.code]: forRoute }
        })
        toast.error(`Could not assign ${field}`, {
          description: `${route.code} is unchanged. Please try again.`,
        })
      } finally {
        setSaving((current) => {
          const next = { ...current }
          delete next[cellKey]
          return next
        })
      }
    },
    [byRouteCode, updateAssignment]
  )

  /** See plannedDriverClipboardValue for which spelling is copied. */
  const handleCopyPlanned = useCallback(
    async (key: string, planned: PlannedDriver, option: DrbOption | undefined) => {
      const value = plannedDriverClipboardValue(planned, option)
      try {
        await navigator.clipboard.writeText(value)
        setCopiedKey(key)
        window.setTimeout(
          () => setCopiedKey((current) => (current === key ? null : current)),
          1500
        )
      } catch {
        toast.error("Could not copy", { description: `Copy ${value} by hand.` })
      }
    },
    []
  )

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase()

    const built = routes.map((route) => {
      const assignment = byRouteCode.get(route.code)
      const planned = plannedFor(route.id)
      const driver = cellValue(route, "driver", assignment)
      const vehicle = cellValue(route, "vehicle", assignment)
      const trailer = cellValue(route, "trailer", assignment)
      const secondDriver = cellValue(route, "second_driver", assignment)
      const state = assignmentState(driver.name, vehicle.name, route.crewSize, secondDriver.name)
      const isCancelled = route.status === "cancelled"
      return {
        route,
        assignment,
        planned,
        driver,
        vehicle,
        trailer,
        secondDriver,
        state,
        isCancelled,
        // Null only when the check ran on this route, the only case the cell may say "No shift".
        notChecked: coverNotCheckedReason(route),
        urgency: isCancelled
          ? ("none" as AssignmentUrgency)
          : assignmentUrgency(state, route.plannedStartTime, route.actualStartTime, now),
        text: {
          code: route.code,
          start: format(route.plannedStartTime, "HH:mm"),
          planned: planned.map((p) => p.name).join(" "),
          driver: driver.name ?? "",
          vehicle: vehicle.name ?? "",
          trailer: trailer.name ?? "",
        } as Record<ColumnKey, string>,
      }
    })

    const matching = q
      ? built.filter((r) => Object.values(r.text).some((v) => v.toLowerCase().includes(q)))
      : built

    const ASSIGNMENT_ORDER = { Unassigned: 0, "Part-assigned": 1, Assigned: 2 } as const

    const compare = (a: (typeof built)[number], b: (typeof built)[number]) => {
      if (sortKey === "start") return a.route.plannedStartTime.getTime() - b.route.plannedStartTime.getTime()
      if (sortKey === "assignment")
        return (
          (ASSIGNMENT_ORDER[a.state.label as keyof typeof ASSIGNMENT_ORDER] ?? 3) -
          (ASSIGNMENT_ORDER[b.state.label as keyof typeof ASSIGNMENT_ORDER] ?? 3)
        )
      // Empty cells sort last in both directions.
      const left = a.text[sortKey]
      const right = b.text[sortKey]
      if (!left && !right) return 0
      if (!left) return 1
      if (!right) return -1
      return left.localeCompare(right)
    }

    const sorted = [...matching].sort(compare)
    return sortDesc ? sorted.reverse() : sorted
  }, [routes, search, sortKey, sortDesc, byRouteCode, plannedFor, cellValue, now])

  const handleSort = useCallback((key: SortKey) => {
    setSortKey((current) => {
      if (current === key) {
        setSortDesc((d) => !d)
        return current
      }
      setSortDesc(false)
      return key
    })
  }, [])

  // Excludes cancelled rows, which need nobody.
  const unassignedCount = rows.filter((r) => !r.isCancelled && !r.driver.name).length
  const urgentCount = rows.filter((r) => r.urgency !== "none").length

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden bg-palette-jungleGreen000/40 p-4">
      <div className="flex flex-shrink-0 items-center gap-3">
        <div className="relative w-72">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-palette-jungleGreen400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search route, driver, vehicle…"
            className="h-8 border-palette-jungleGreen200 bg-white pl-9 text-sm placeholder:text-palette-jungleGreen400"
          />
        </div>
        {search && (
          <Button
            variant="outline"
            onClick={() => setSearch("")}
            className="h-8 gap-1.5 rounded-md border-palette-jungleGreen200 bg-white px-2.5 text-sm"
          >
            <X size={12} />
            Clear
          </Button>
        )}
        <Button
          variant="outline"
          onClick={() => setShowCancelled((v) => !v)}
          aria-pressed={showCancelled}
          className={cn(
            "h-8 gap-1.5 rounded-md border-palette-jungleGreen200 bg-white px-2.5 text-sm",
            showCancelled &&
              "border-palette-jungleGreen300 bg-palette-jungleGreen100 text-palette-jungleGreen900"
          )}
        >
          <Ban size={12} />
          Show cancelled
        </Button>
        <span className="text-sm text-palette-jungleGreen600">
          {rows.length} {rows.length === 1 ? "route" : "routes"}
          {unassignedCount > 0 && (
            <>
              {" · "}
              <span className="font-medium text-palette-honeycomb800">
                {unassignedCount} without a driver
              </span>
            </>
          )}
          {urgentCount > 0 && (
            <>
              {" · "}
              <button
                type="button"
                onClick={() => handleSort("start")}
                className="font-medium text-variance-late underline-offset-2 hover:underline"
              >
                {urgentCount} needing attention now
              </button>
            </>
          )}
        </span>
        <span className="ml-auto text-sm font-medium text-palette-jungleGreen600">
          {formatDayShortYear(today)}
        </span>
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-palette-jungleGreen200 bg-white shadow-sm">
        <div className="min-w-0 flex-shrink-0">
          <div
            className="grid border-b border-palette-jungleGreen200 bg-palette-jungleGreen000"
            style={{ gridTemplateColumns: GRID_TEMPLATE }}
          >
            {COLUMNS.map((column, index) => (
              <div
                key={column.key}
                className={cn(
                  CELL,
                  "h-10 text-[13px] font-semibold uppercase tracking-wide text-palette-jungleGreen700",
                  index === 0 && KEY_CELL
                )}
              >
                <SortHeader
                  label={column.label}
                  sortKey={column.key}
                  activeSort={sortKey}
                  sortDesc={sortDesc}
                  onSort={handleSort}
                />
              </div>
            ))}
            <div
              className={cn(
                CELL,
                "h-10 border-r-0 text-[13px] font-semibold uppercase tracking-wide text-palette-jungleGreen700"
              )}
            >
              <SortHeader
                label="Assignment"
                sortKey="assignment"
                activeSort={sortKey}
                sortDesc={sortDesc}
                onSort={handleSort}
              />
            </div>
          </div>
        </div>

        <ScrollArea className="min-h-0 flex-1">
          {isRoutesLoading ? (
            <LoadingState label="Loading routes…" className="py-12" />
          ) : rows.length === 0 ? (
            <EmptyState>
              {routes.length === 0 ? "No routes scheduled today." : "No routes match that search."}
            </EmptyState>
          ) : (
            rows.map(
              ({
                route,
                planned,
                driver,
                vehicle,
                trailer,
                secondDriver,
                state,
                urgency,
                isCancelled,
                notChecked,
              }) => {
              const plannedPeople = planned.map((person) => {
                const option = matchPlannedDriverOption(person, driverByPersonId)
                return {
                  person,
                  option,
                  key: `${route.id}:${person.personId ?? person.name}`,
                  seat: plannedDriverSeat(option, driver.id, secondDriver.id, route.crewSize),
                }
              })
              const urgent = urgency !== "none" ? urgency : null

              return (
                <div
                  key={route.id}
                  className={cn(
                    "group grid border-b border-palette-jungleGreen100 last:border-b-0",
                    urgent ? URGENCY_ROW[urgent] : "hover:bg-palette-jungleGreen000/60",
                    isCancelled && "bg-palette-jungleGreen000/40 text-palette-jungleGreen400"
                  )}
                  style={{ gridTemplateColumns: GRID_TEMPLATE }}
                >
                  <div
                    className={cn(
                      CELL,
                      urgent ? "relative pl-3.5" : KEY_CELL,
                      "font-mono text-[13px] font-medium",
                      isCancelled
                        ? "text-palette-jungleGreen400"
                        : "text-palette-jungleGreen900"
                    )}
                  >
                    {urgent && (
                      <span
                        aria-hidden
                        className={cn(
                          "absolute inset-y-0 left-0 w-1.5",
                          URGENCY_BAR[urgent]
                        )}
                      />
                    )}
                    <span
                      className={cn("truncate", isCancelled && "line-through opacity-70")}
                      title={route.code}
                    >
                      {route.code}
                    </span>
                  </div>
                  <div
                    className={cn(
                      CELL,
                      "text-sm font-medium tabular-nums",
                      isCancelled
                        ? "text-palette-jungleGreen400"
                        : "text-palette-jungleGreen900"
                    )}
                  >
                    {format(route.plannedStartTime, "HH:mm")}
                  </div>
                  <div className={cn(CELL, "flex-col items-stretch gap-1")}>
                    {plannedPeople.length > 0 ? (
                      plannedPeople.map(({ person, option, key, seat }) => (
                        <div key={key} className="flex min-w-0 items-center gap-1">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <button
                                type="button"
                                onClick={() => handleCopyPlanned(key, person, option)}
                                className="min-w-0 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-palette-blueGreen700"
                                aria-label={`Copy ${person.name} to the clipboard`}
                              >
                                <Chip
                                  className={cn(
                                    "block cursor-pointer hover:bg-palette-jungleGreen200",
                                    copiedKey === key &&
                                      "bg-palette-blueGreen100 text-palette-blueGreen800"
                                  )}
                                >
                                  {copiedKey === key ? "Copied" : person.name}
                                </Chip>
                              </button>
                            </TooltipTrigger>
                            <TooltipContent>
                              {option
                                ? `Copy "${option.name}" to paste into Driver`
                                : `Copy "${person.name}" to paste into Driver`}
                            </TooltipContent>
                          </Tooltip>
                          {seat && option && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  aria-label={
                                    seat === "second_driver"
                                      ? `Assign ${person.name} as the second driver`
                                      : `Assign ${person.name} as the driver`
                                  }
                                  onClick={() => handleAssign(route, seat, option.id, option.name)}
                                  disabled={!!saving[`${route.id}:${seat}`]}
                                  // Hidden until hover or focus, but still keyboard-reachable.
                                  className="h-6 w-6 flex-none rounded p-0 text-palette-blueGreen700 opacity-0 hover:bg-palette-blueGreen100 focus-visible:opacity-100 group-hover:opacity-100"
                                >
                                  <CornerDownRight size={14} />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                {seat === "second_driver"
                                  ? "Assign to Second driver"
                                  : "Assign to Driver"}
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      ))
                    ) : notChecked ? (
                      // Say why the check was silent, so a blank never reads as nobody booked.
                      <span
                        className="truncate pr-1 text-sm italic text-palette-jungleGreen400"
                        title={notChecked.why}
                      >
                        {notChecked.label}
                      </span>
                    ) : shiftError ? (
                      <span
                        className="truncate pr-1 text-sm italic text-palette-jungleGreen400"
                        title={(shiftError as Error).message}
                      >
                        Check unavailable
                      </span>
                    ) : (
                      <span className="text-sm text-palette-jungleGreen400">No shift</span>
                    )}
                  </div>
                  {/* Both drivers stack in one cell; a separate column would be empty on almost
                      every row. */}
                  <div className={cn(CELL, "flex-col items-stretch gap-1")}>
                    {isCancelled ? (
                      <span className="truncate text-sm text-palette-jungleGreen400">
                        {driver.name ?? "—"}
                      </span>
                    ) : (
                      <AssignmentCombobox
                        options={drivers}
                        value={driver.id}
                        displayValue={driver.name}
                        onSelect={(id, name) => handleAssign(route, "driver", id, name)}
                        placeholder="Select driver…"
                        searchPlaceholder="Search drivers…"
                        isLoading={isDrbLoading}
                        isSaving={!!saving[`${route.id}:driver`]}
                        triggerClassName="h-8"
                        open={openPicker === `${route.id}:driver`}
                        onOpenChange={(isOpen) => setOpenPicker(isOpen ? `${route.id}:driver` : null)}
                      />
                    )}
                    {route.crewSize > 1 &&
                      (isCancelled ? (
                        <span className="truncate text-sm text-palette-jungleGreen400">
                          {secondDriver.name ?? "—"}
                        </span>
                      ) : (
                        <AssignmentCombobox
                          options={drivers}
                          value={secondDriver.id}
                          displayValue={secondDriver.name}
                          onSelect={(id, name) => handleAssign(route, "second_driver", id, name)}
                          placeholder="Select second driver…"
                          searchPlaceholder="Search drivers…"
                          isLoading={isDrbLoading}
                          isSaving={!!saving[`${route.id}:second_driver`]}
                          triggerClassName="h-8"
                          open={openPicker === `${route.id}:second_driver`}
                          onOpenChange={(isOpen) =>
                            setOpenPicker(isOpen ? `${route.id}:second_driver` : null)
                          }
                        />
                      ))}
                  </div>
                  <div className={CELL}>
                    {isCancelled ? (
                      <span className="truncate text-sm text-palette-jungleGreen400">
                        {vehicle.name ?? "—"}
                      </span>
                    ) : (
                      <AssignmentCombobox
                        options={vehicles}
                        value={vehicle.id}
                        displayValue={vehicle.name}
                        onSelect={(id, name) => handleAssign(route, "vehicle", id, name)}
                        placeholder="Select vehicle…"
                        searchPlaceholder="Search vehicles…"
                        isLoading={isDrbLoading}
                        isSaving={!!saving[`${route.id}:vehicle`]}
                        triggerClassName="h-8"
                        open={openPicker === `${route.id}:vehicle`}
                        onOpenChange={(isOpen) => setOpenPicker(isOpen ? `${route.id}:vehicle` : null)}
                      />
                    )}
                  </div>
                  <div className={CELL}>
                    {isCancelled ? (
                      <span className="truncate text-sm text-palette-jungleGreen400">
                        {trailer.name ?? "—"}
                      </span>
                    ) : (
                      <AssignmentCombobox
                        options={trailers}
                        value={trailer.id}
                        displayValue={trailer.name}
                        onSelect={(id, name) => handleAssign(route, "trailer", id, name)}
                        placeholder="—"
                        searchPlaceholder="Search trailers…"
                        isLoading={isDrbLoading}
                        isSaving={!!saving[`${route.id}:trailer`]}
                        triggerClassName="h-8"
                        open={openPicker === `${route.id}:trailer`}
                        onOpenChange={(isOpen) =>
                          setOpenPicker(isOpen ? `${route.id}:trailer` : null)
                        }
                      />
                    )}
                  </div>
                  <div className={cn(CELL, "border-r-0")}>
                    {isCancelled ? (
                      <StatusPill tone="muted" label="Cancelled" size="sm" />
                    ) : urgent ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span>
                            <StatusPill
                              tone={urgent === "overdue" ? "danger" : "warning"}
                              label={urgent === "overdue" ? "Overdue" : "Starts soon"}
                              withDot
                            />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{URGENCY_NOTE[urgent]}</TooltipContent>
                      </Tooltip>
                    ) : (
                      <StatusPill tone={state.tone} label={state.label} withDot />
                    )}
                  </div>
                  </div>
                )
              }
            )
          )}
        </ScrollArea>
      </div>
    </div>
  )
}

export default RouteAssignmentListView
